import { parseEther } from 'viem';
import { CREATOR_DEPOSIT } from '../config/contracts';

/** Matches AAConfig default `relaunchMultiplier` — never use 2 here. */
export const DEFAULT_RELAUNCH_MULTIPLIER = 3n;

/**
 * Factory `relaunch` required wei for a parent at `parentEpoch` (1-based).
 * Same formula as AAMarket lineage: launchCostSnapshot * multiplier^parentEpoch
 * (= parent.effectiveLaunchCost * parent.relaunchMultiplierSnapshot).
 */
export function computeRelaunchCostWei(
  parentEpoch: number,
  launchCost?: bigint | null,
  relaunchMultiplier?: bigint | null,
): bigint {
  const baseCost = launchCost != null ? BigInt(launchCost.toString()) : parseEther(CREATOR_DEPOSIT.toString());
  const mult =
    relaunchMultiplier != null ? BigInt(relaunchMultiplier.toString()) : DEFAULT_RELAUNCH_MULTIPLIER;
  let cost = baseCost;
  const epoch = Math.max(1, parentEpoch);
  for (let i = 1; i < epoch + 1; i++) {
    cost = cost * mult;
  }
  return cost;
}

/** Prefer `factory.quoteRelaunch` (frozen lineage). Falls back to 0n if the quote is missing. */
export function costFromQuoteRelaunch(quote: unknown): bigint {
  if (quote == null) return 0n;
  const cost = Array.isArray(quote)
    ? quote[0]
    : typeof quote === 'object' && quote !== null && 'cost' in quote
      ? (quote as { cost?: bigint }).cost
      : undefined;
  if (cost == null) return 0n;
  const n = BigInt(cost.toString());
  return n > 0n ? n : 0n;
}

export function maxRelaunchFromQuote(quote: unknown): number | null {
  if (quote == null) return null;
  const cap = Array.isArray(quote)
    ? quote[2]
    : typeof quote === 'object' && quote !== null && 'maxRelaunch' in quote
      ? (quote as { maxRelaunch?: bigint }).maxRelaunch
      : undefined;
  if (cap == null) return null;
  const n = Number(cap);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Relaunch txs must use on-chain config — do not submit with hardcoded fallbacks. */
export function isRelaunchCostReady(
  launchCost: bigint | undefined | null,
  relaunchMultiplier: bigint | undefined | null,
): boolean {
  return launchCost != null && relaunchMultiplier != null;
}

/** Map common factory revert strings to user-facing copy. */
export function relaunchRevertMessage(raw: string): string {
  const s = raw.toLowerCase();
  if (s.includes('must send exact relaunch cost')) {
    return 'Relaunch cost mismatch — refresh the page and try again.';
  }
  if (s.includes('closer privilege active')) {
    return 'Only the wallet that closed this market can relaunch during the exclusive window.';
  }
  if (s.includes('parent not closed')) {
    return 'Market is not finalized on-chain yet — wait a few seconds and try again.';
  }
  if (s.includes('closed by expiry, not votes')) {
    return 'This market closed on the timer, not at max votes — relaunch is not available.';
  }
  if (s.includes('max relaunches reached')) {
    return 'This tweet has reached the maximum number of relaunches.';
  }
  if (s.includes('parent config mismatch')) {
    return 'This market was created by a different contract deployment (config) than the current factory, so it cannot be relaunched here. It must be relaunched with the factory/config it was created under.';
  }
  if (s.includes('unknown parent') || s.includes('parent not registered')) {
    return 'This market is not on the official factory registry, so it cannot be relaunched here.';
  }
  if (s.includes('already relaunched')) {
    return 'This round has already been relaunched. Open the latest round instead.';
  }
  return raw.length > 120 ? `${raw.slice(0, 117)}…` : raw;
}
